import React, {
  FunctionComponent,
  useEffect,
  useReducer,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  answerQuestion,
  async_createGameForQuizz,
  async_fetchQuestions,
  async_finishGame,
  chooseGift,
  nextQuestion,
  recomputeScore,
  timeElapsed,
  startQuizz,
} from '../../redux/actions/quizz'
import WheelButton from '../../components/cta/WheelButton'
import {
  computeScoreForMultiAnswers,
  QUIZZ_STEP_TRIPADVISOR,
  QUIZZ_STEP_HOME,
  QUIZZ_STEP_END,
  QUIZZ_STEP_PLAY,
  QUIZZ_STEP_ML_INTRO,
  QUIZZ_STEP_ML_GAME,
} from '../../redux/reducers/quizz'
import { Modal } from 'react-bootstrap'
import './styles.scss'
import { tt } from '../../i18n'
import Tuto from '../../components/tuto/Tuto'
import LotteryV2StepType from '@tootsweet/model/lottery/LotteryV2StepType'
import { QuizzGift } from '@tootsweet/model/lottery/Quizz'
import EmailAddressModal from '../03_EmailAddressModal/EmailAddressModal'
import { setShowFooter } from '../../redux/actions/app'
import WheelAnalyticsManager from '../../analytics/WheelAnalyticsManager'
import { buildRedeemURL, offerToFriend } from '../../redux/actions/lottery'
import QuizzNormalSkin, { getSkin, QuizzSkinI } from './skins/default'
import OfferToFriend from '../../components/OfferToFriend'
import MonthlyLotteryIntro from '../14_MonthlyLotteryIntro/MonthlyLotteryIntro'
import MonthlyLotteryGame from '../14_MonthlyLotteryIntro/MonthlyLotteryGame'
import QuizzLogo from './Components/QuizzLogo'
import QuizzHome from './Components/QuizzHome'
import { PROGRESS_BAR_TOP } from './constants'
import Quizz from './Components/Quizz'
import QuizzEnd from './Components/QuizzEnd'
import QuizzTripAdvisor from './Components/QuizzTripAdvisor'
import useModalControls from '../../hooks/useModalControls'
import {
  ctaClickCountSelector,
  lotteryConfigSelector,
  lotteryGameConsumeSelector,
  lotteryStepSelector,
  tutoIsIGSelector,
  showTutoSelector,
  tutoIsIGStorySelector,
  tutoRenderCTASelector,
  tutoIsGoogleSelector,
} from '../../redux/selectors/lottery'
import {
  hasReachedTerminalScoreSelector,
  quizzSelector,
  giftsSelector,
  scoreSelector,
  currentQuestionSelector,
  quizzStepSelector,
  chosenGiftSelector,
} from '../../redux/selectors/quizz'
import QuizzStep from './Components/QuizzStep'
import { gameInitialState, gameReducer } from './reducers/gameReducer'
import { timerInitialState, timerReducer } from './reducers/timerReducer'
import QuizzScore from './Components/QuizzScore'

interface Props {
  finalPlaceName: string
  isIOS: boolean
}

const QuizzApp: FunctionComponent<Props> = (props) => {
  const [progressBarPos, setProgressBarPos] = useState(PROGRESS_BAR_TOP)
  const [hasStep, setHasStep] = useState(false)
  const [hasPerformedStep, setHasPerformedStep] = useState(false)
  const [skin, setSkin] = useState<QuizzSkinI>(new QuizzNormalSkin())
  const [hasClickedNo, setHasClickedNo] = useState(false)

  const [gameState, gameDispatch] = useReducer(gameReducer, gameInitialState)

  const [timerState, timerDispatch] = useReducer(
    timerReducer,
    timerInitialState
  )

  const { isOpen, openModal, closeModal } = useModalControls({
    onOpenModal: () => {
      WheelAnalyticsManager.logEvent(
        `${lotteryStep?.stepType?.toLowerCase()}_modal`
      )
    },
  })

  const emailModalControls = useModalControls({})

  const dispatch = useDispatch()

  const lotteryConfig = useSelector(lotteryConfigSelector)
  const lotteryStep = useSelector(lotteryStepSelector)
  const lotteryGame = useSelector(lotteryGameConsumeSelector)
  const hasReachedTerminalScore = useSelector(hasReachedTerminalScoreSelector)
  const quizz = useSelector(quizzSelector)
  const quizzStep = useSelector(quizzStepSelector)
  const currentQuestion = useSelector(currentQuestionSelector)
  const gifts = useSelector(giftsSelector)
  const score = useSelector(scoreSelector)
  const ctaClickCount = useSelector(ctaClickCountSelector)
  const showTuto = useSelector(showTutoSelector)
  const tutoIsIG = useSelector(tutoIsIGSelector)
  const tutoIsIGStory = useSelector(tutoIsIGStorySelector)
  const tutoIsGoogle = useSelector(tutoIsGoogleSelector)
  const tutoRenderCTA = useSelector(tutoRenderCTASelector)
  const chosenGift = useSelector(chosenGiftSelector)

  const onContinueClicked = () => {
    if (hasReachedTerminalScore) {
      const giftsThatApply = getGiftsThatApply()
      if (giftsThatApply.length === 0) {
        alert(tt('error'))
        return
      }
      onGiftChosen(giftsThatApply[0])
      return
    }
    if (quizz?.multi && !gameState.animatePostAnswer) {
      timerDispatch({ type: 'timer_stop' })
      gameDispatch({ type: 'question_answer_no_payload' })
      dispatch(recomputeScore())
    } else {
      if (currentQuestion?.audio) {
        timerDispatch({
          type: 'timer_reset',
          payload: lotteryConfig?.qTimerSeconds ?? 0,
        })
      } else {
        timerDispatch({
          type: 'timer_start',
          payload: {
            interval: () => {
              timerDispatch({ type: 'timer_tick' })
            },
            remainingSeconds: lotteryConfig?.qTimerSeconds ?? 0,
          },
        })
      }
      gameDispatch({ type: 'question_next' })
      dispatch(nextQuestion())
    }
  }

  const getGiftsThatApply = (): QuizzGift[] => {
    const giftsThatApply: QuizzGift[] = []
    let minScoreToWin = Number.MAX_SAFE_INTEGER

    gifts.forEach((gift: QuizzGift) => {
      if (gift.l < minScoreToWin) {
        minScoreToWin = gift.l
      }
      if (score >= gift.l && score <= gift.u) {
        giftsThatApply.push(gift)
      }
    })
    return giftsThatApply
  }

  const onGiftChosen = (gift: QuizzGift) => {
    dispatch(chooseGift(gift))
    emailModalControls.openModal()
  }

  const onEmailModalFinished = async () => {
    if (lotteryGame) {
      await dispatch(async_finishGame())
      emailModalControls.closeModal()

      // if (props.hasReachedTerminalScore) {
      //   window.location.href = buildRedeemURL(props.lotteryGame.id)
      // } else {
      //   props.goToMarketingStep()
      // }

      window.location.href = buildRedeemURL(lotteryGame.id)
    } else {
      alert(tt('error'))
    }
  }

  const answerWithIndex = async (idx: number) => {
    if (quizz?.multi) {
      if (
        quizz.maxAnswers > 0 &&
        currentQuestion?.clientSideAnswersIdx &&
        quizz.maxAnswers === currentQuestion.clientSideAnswersIdx.length
      ) {
        return
      }

      dispatch(answerQuestion(idx))
    } else {
      if (gameState.answered) {
        return
      }
      dispatch(answerQuestion(idx))
      timerDispatch({ type: 'timer_stop' })
      gameDispatch({ type: 'question_answer', payload: idx })
    }
  }

  const _startQuizz = () => {
    if (hasStep && !hasPerformedStep) {
      WheelAnalyticsManager.logEvent(lotteryStep?.stepType)
      openModal()
    } else {
      WheelAnalyticsManager.logEvent('start_quizz')
      dispatch(startQuizz())
      if (!gameState.gameCreated) {
        dispatch(async_createGameForQuizz())
        gameDispatch({ type: 'game_start' })
      }
    }
    dispatch(setShowFooter(false))
  }

  useEffect(() => {
    if (timerState.remainingSeconds <= 0 && timerState.isRunning) {
      timerDispatch({ type: 'timer_stop' })
      gameDispatch({ type: 'question_timeout' })
      dispatch(timeElapsed())
    }
  }, [timerState])

  const onClickPlayAudio = () => {
    timerDispatch({
      type: 'timer_start',
      payload: {
        interval: () => {
          timerDispatch({ type: 'timer_tick' })
        },
        remainingSeconds: lotteryConfig?.qTimerSeconds ?? 0,
      },
    })
    gameDispatch({ type: 'question_start' })
  }

  useEffect(() => {
    dispatch(async_fetchQuestions())
    dispatch(setShowFooter(true))
  }, [])

  useEffect(() => {
    if (!lotteryConfig?.steps) return
    setSkin(getSkin(quizz?.skin))
  }, [quizz, lotteryConfig?.steps])

  useEffect(() => {
    // Performing step triggers ctaClickCount increment thus closing the modal
    setHasPerformedStep(true)
    closeModal()
  }, [ctaClickCount])

  useEffect(() => {
    if (!lotteryConfig?.steps) return
    const nSteps = lotteryConfig?.steps?.length || 0
    if (lotteryConfig?.hasML) {
      if (nSteps > 3 && !hasStep) {
        setHasStep(true)
        // setCurrentStep(lotteryConfig?.steps[2]);
        setHasPerformedStep(false)
      }
    } else if (nSteps > 1 && !hasStep) {
      setHasStep(true)
      // setCurrentStep(lotteryConfig?.steps[0]);
      setHasPerformedStep(false)
    }
  }, [lotteryConfig])

  useEffect(() => {
    if (quizzStep === QUIZZ_STEP_PLAY) {
      if (currentQuestion?.audio) {
        gameDispatch({ type: 'question_hide' })
      } else {
        gameDispatch({ type: 'question_next' })
        if (!timerState.id) {
          timerDispatch({
            type: 'timer_start',
            payload: {
              interval: () => {
                timerDispatch({ type: 'timer_tick' })
              },
              remainingSeconds: lotteryConfig?.qTimerSeconds ?? 0,
            },
          })
        }
      }
    }
    if (quizzStep === QUIZZ_STEP_TRIPADVISOR) {
      dispatch(setShowFooter(true))
    }
    if (quizzStep === QUIZZ_STEP_END) {
      timerDispatch({ type: 'timer_stop' })
    }
  }, [quizzStep, currentQuestion?.audio])

  useEffect(() => {
    if (
      currentQuestion &&
      !!currentQuestion.audio &&
      progressBarPos !== PROGRESS_BAR_TOP
    ) {
      setProgressBarPos(PROGRESS_BAR_TOP)
    }
  }, [currentQuestion, currentQuestion?.audio])

  let continueButtonText = !!currentQuestion?.audio
    ? tt('quizz_next_question_audio')
    : tt('quizz_next_question')

  if (hasReachedTerminalScore) {
    continueButtonText = tt('lp_discover_my_gift')
  }

  if (quizzStep === QUIZZ_STEP_ML_INTRO) {
    return (
      <>
        <QuizzLogo
          containerClass={'logo-container logo-container-centered'}
          useConfig={true}
        />
        <MonthlyLotteryIntro isQuizz={true} />
      </>
    )
  }

  if (quizzStep === QUIZZ_STEP_ML_GAME) {
    return (
      <>
        <QuizzLogo
          containerClass={'logo-container logo-container-centered'}
          useConfig={true}
        />
        <MonthlyLotteryGame isQuizz={true} />
      </>
    )
  }

  if (lotteryStep?.stepType === LotteryV2StepType.Offer)
    return <OfferToFriend />

  return (
    <>
      <div id="quizz">
        {quizzStep === QUIZZ_STEP_HOME && skin?.homeHeaderDecoration()}

        {quizzStep === QUIZZ_STEP_PLAY && (
          <QuizzScore winScore={quizz?.winScore} />
        )}

        <div className="quizz-main cm-pad no-gutters" style={{ flex: 1 }}>
          {quizzStep === QUIZZ_STEP_HOME && (
            <QuizzHome
              hasStepAndPerformedIt={hasStep && hasPerformedStep}
              skin={skin}
              startQuizz={_startQuizz}
            />
          )}
          {quizzStep === QUIZZ_STEP_PLAY && (
            <Quizz
              progressBarPos={progressBarPos}
              timer={timerState}
              game={gameState}
              onClickPlayAudio={onClickPlayAudio}
              answerWithIndex={answerWithIndex}
              answerIsCorrect={
                quizz?.multi
                  ? computeScoreForMultiAnswers(currentQuestion) > 0
                  : currentQuestion?.answerIdx === gameState.answeredIndex
              }
            />
          )}
          {quizzStep === QUIZZ_STEP_TRIPADVISOR &&
            (!!lotteryConfig?.tripAdvisorLink ? (
              <QuizzTripAdvisor finalPlaceName={props.finalPlaceName} />
            ) : (
              <QuizzEnd
                onGiftChosen={onGiftChosen}
                getGiftsThatApply={getGiftsThatApply}
              />
            ))}
          {quizzStep === QUIZZ_STEP_END && (
            <QuizzEnd
              onGiftChosen={onGiftChosen}
              getGiftsThatApply={getGiftsThatApply}
            />
          )}
        </div>

        {(quizzStep === QUIZZ_STEP_PLAY ||
          quizzStep === QUIZZ_STEP_TRIPADVISOR) && (
          <div className="quizz-progress-bar">
            {quizzStep === QUIZZ_STEP_PLAY &&
              (quizz?.multi || gameState.answered) && (
                <WheelButton
                  text={continueButtonText}
                  isInverted={true}
                  onClick={onContinueClicked}
                />
              )}
          </div>
        )}
      </div>

      {skin?.appendToBody(quizzStep)}

      {lotteryStep && (
        <Modal
          show={isOpen}
          onHide={closeModal}
          centered={true}
          backdrop="static"
        >
          <Modal.Body>
            <div className="w-modal">
              <div className="container">
                <i className="fas fa-times modal-close" onClick={closeModal} />
                <QuizzStep
                  finalPlaceName={props.finalPlaceName}
                  hasClickedNo={hasClickedNo}
                  setHasClickedNo={setHasClickedNo}
                  isIOS={props.isIOS}
                />
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}

      <Tuto
        show={showTuto}
        isIOS={props.isIOS}
        isIG={tutoIsIG}
        isIGStory={tutoIsIGStory}
        isGoogle={tutoIsGoogle}
        renderCTA={tutoRenderCTA ?? function () {}}
      />

      {emailModalControls.isOpen && (
        <EmailAddressModal
          show={true}
          closeModal={emailModalControls.closeModal}
          onFinished={onEmailModalFinished}
          prizeLabel={chosenGift?.longName || chosenGift?.label}
          offerToFriend={(url: string) => {
            dispatch(offerToFriend(url))
          }}
        />
      )}
    </>
  )
}

export default QuizzApp
